/**
 * CommissionInvoicesPage
 * ======================
 * Admin page to generate and manage monthly commission invoices for vendors.
 *
 * Features:
 *  - Month picker + vendor filter
 *  - Generate individual or bulk invoices
 *  - Per-invoice line items with GST breakdown
 *  - Send email to vendor
 *  - Razorpay payment link creation + copy
 *  - HTML invoice preview
 */

import { useState, useEffect, useCallback } from 'react';
import JSZip from 'jszip';
import { useAuth } from '../../context/AuthContext';
import DashboardLayout from '../../components/DashboardLayout';
import {
  Receipt, Send, RefreshCw, ExternalLink, ChevronDown, ChevronUp,
  Copy, Check, Mail, Loader2, AlertCircle, FileText, Zap, Clock,
  History, PlayCircle, Download, Plus,
} from 'lucide-react';
import { Button }   from '../../components/ui/button';
import { Badge }    from '../../components/ui/badge';
import { Input }    from '../../components/ui/input';
import { Checkbox } from '../../components/ui/checkbox';
import { toast }    from '../../components/ui/sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import { CommissionCreditNotesTab } from '../../components/admin/CommissionCreditNotesTab';
import ManualCommissionDocumentModal from '../../components/admin/ManualCommissionDocumentModal';
import ExportLedgerButton from '../../components/admin/ExportLedgerButton';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function fmt(n) {
  return `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function monthLabel(m) {
  if (!m) return '';
  const [y, mo] = m.split('-');
  return new Date(parseInt(y), parseInt(mo) - 1).toLocaleString('default', { month: 'long', year: 'numeric' });
}

function statusBadge(status) {
  const map = {
    finalized: 'bg-blue-100 text-blue-700',
    issued:    'bg-indigo-100 text-indigo-700 font-semibold',
    sent:      'bg-green-100 text-green-700',
    paid:      'bg-emerald-100 text-emerald-800 font-semibold',
    draft:     'bg-gray-100 text-gray-600',
  };
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs ${map[status] || 'bg-gray-100 text-gray-500'}`}>
      {status}
    </span>
  );
}

// ─── Sub-components ──────────────────────────────────────────────────────────

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  function copy() {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }
  return (
    <button onClick={copy} className="ml-2 text-gray-400 hover:text-gray-700 transition-colors" title="Copy">
      {copied ? <Check className="h-3.5 w-3.5 text-green-600" /> : <Copy className="h-3.5 w-3.5" />}
    </button>
  );
}

function InvoiceLineItems({ items }) {
  return (
    <table className="w-full text-xs mt-3" data-testid="invoice-line-items">
      <thead>
        <tr className="bg-gray-50 text-gray-500 text-left">
          <th className="py-2 px-3 font-medium">Order</th>
          <th className="py-2 px-3 font-medium text-right">Sale</th>
          <th className="py-2 px-3 font-medium text-right">Rate</th>
          <th className="py-2 px-3 font-medium text-right">Commission</th>
          <th className="py-2 px-3 font-medium text-right">GST 18%</th>
          <th className="py-2 px-3 font-medium text-right">Total</th>
        </tr>
      </thead>
      <tbody>
        {items.map((li, i) => (
          <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'} data-testid={`invoice-line-item-${i}`}>
            <td className="py-2 px-3 text-gray-700">
              #{li.order_number || li.order_id?.slice(0, 8)}
              {li.promo_note && (
                <div className="text-[10px] text-blue-600 font-normal mt-0.5" data-testid={`zero-commission-note-${i}`}>
                  {li.promo_note}
                </div>
              )}
            </td>
            <td className="py-2 px-3 text-right text-gray-600">
              {fmt(li.gross_total)}
              {li.commission_base != null && Math.abs(li.commission_base - li.gross_total) >= 0.01 && (
                <div className="text-[10px] text-amber-600 font-normal" title="Commission is calculated on product value only, excluding shipping/pass-through">
                  on {fmt(li.commission_base)} product value
                </div>
              )}
            </td>
            <td className="py-2 px-3 text-right text-gray-500">{li.commission_rate_pct?.toFixed(1)}%</td>
            <td className="py-2 px-3 text-right text-gray-700">{fmt(li.commission)}</td>
            <td className="py-2 px-3 text-right text-amber-700">{fmt(li.commission_gst)}</td>
            <td className="py-2 px-3 text-right font-semibold text-gray-900">{fmt(li.line_total)}</td>
          </tr>
        ))}
      </tbody>
      <tfoot>
        <tr className="border-t border-gray-200">
          <td colSpan={3} className="py-2 px-3 text-xs text-gray-400">SAC: 996211</td>
          <td className="py-2 px-3 text-right font-semibold text-gray-800 text-xs">
            {fmt(items.reduce((s, l) => s + l.commission, 0))}
          </td>
          <td className="py-2 px-3 text-right font-semibold text-amber-700 text-xs">
            {fmt(items.reduce((s, l) => s + l.commission_gst, 0))}
          </td>
          <td className="py-2 px-3 text-right font-bold text-gray-900 text-xs">
            {fmt(items.reduce((s, l) => s + l.line_total, 0))}
          </td>
        </tr>
      </tfoot>
    </table>
  );
}

function InvoiceCard({ inv, onSendEmail, onCreateLink, onPreview, onDownload, onPreviewAdvice, onDownloadAdvice, selected, onToggleSelect }) {
  const [expanded, setExpanded] = useState(false);
  const [sending, setSending]   = useState(false);
  const [linking, setLinking]   = useState(false);
  const [downloading, setDownloading] = useState(null); // null | 'invoice' | 'advice'

  async function handleSend() {
    setSending(true);
    await onSendEmail(inv.id);
    setSending(false);
  }
  async function handleLink() {
    setLinking(true);
    await onCreateLink(inv.id);
    setLinking(false);
  }
  async function handleDownload() {
    setDownloading('invoice');
    await onDownload(inv.id, inv.invoice_number);
    setDownloading(null);
  }
  async function handleDownloadAdvice() {
    setDownloading('advice');
    await onDownloadAdvice(inv.id, inv.payment_advice_number);
    setDownloading(null);
  }

  return (
    <div
      className="border border-gray-200 rounded-xl bg-white overflow-hidden"
      data-testid={`invoice-card-${inv.id}`}
    >
      {/* Card header */}
      <div className="px-5 py-4 flex items-start justify-between gap-4">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <Checkbox
            checked={!!selected}
            onCheckedChange={() => onToggleSelect(inv.id)}
            className="mt-1"
            data-testid={`select-invoice-${inv.id}`}
          />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-sm font-semibold text-gray-900" data-testid="invoice-number">
              {inv.invoice_number}
            </span>
            {statusBadge(inv.status)}
            {inv.late_declared && (
              <span
                className="px-2 py-0.5 rounded-full text-xs bg-orange-100 text-orange-700 font-medium"
                title="Force-generated after the fact — include in the next GST late-filing/B2B amendment batch"
                data-testid="late-declared-badge"
              >
                Late-Declared
              </span>
            )}
          </div>
          <p className="text-sm text-gray-600 mt-0.5 truncate">
            {inv.vendor_business_name || inv.vendor_name}
            <span className="text-gray-400 ml-2 text-xs">{inv.vendor_email}</span>
          </p>
          <p className="text-xs text-gray-400 mt-1">
            {monthLabel(inv.month)} · {inv.order_count} order{inv.order_count !== 1 ? 's' : ''}
          </p>
        </div>
        </div>

        {/* Totals */}
        <div className="text-right shrink-0">
          <p className="text-xs text-gray-400">Commission + GST</p>
          <p className="text-lg font-bold text-gray-900" data-testid="invoice-total">{fmt(inv.grand_total)}</p>
          <p className="text-xs text-gray-400">
            {fmt(inv.subtotal_commission)} + {fmt(inv.subtotal_gst)} GST
          </p>
        </div>
      </div>

      {/* Payment link row */}
      {inv.payment_link_url && (
        <div className="px-5 pb-3 flex items-center gap-2">
          <a
            href={inv.payment_link_url}
            target="_blank"
            rel="noreferrer"
            className="text-xs text-blue-600 hover:text-blue-800 underline underline-offset-2 flex items-center gap-1"
            data-testid="payment-link"
          >
            <ExternalLink className="h-3 w-3" />
            Payment Link
          </a>
          <CopyButton text={inv.payment_link_url} />
          <span className={`ml-1 text-xs px-1.5 py-0.5 rounded ${
            inv.payment_link_status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
          }`}>
            {inv.payment_link_status || 'created'}
          </span>
          {inv.email_sent_at && (
            <span className="ml-auto text-xs text-green-600 flex items-center gap-1">
              <Mail className="h-3 w-3" /> Sent
            </span>
          )}
        </div>
      )}

      {/* Action bar */}
      <div className="px-5 py-3 bg-gray-50 border-t border-gray-100 flex items-center gap-2 flex-wrap">
        <Button
          variant="outline"
          size="sm"
          className="text-xs h-7"
          onClick={() => setExpanded(!expanded)}
          data-testid="toggle-line-items-btn"
        >
          {expanded ? <ChevronUp className="h-3 w-3 mr-1" /> : <ChevronDown className="h-3 w-3 mr-1" />}
          {expanded ? 'Hide' : 'Line Items'} ({inv.order_count})
        </Button>

        <Button
          variant="outline"
          size="sm"
          className="text-xs h-7"
          onClick={() => onPreview(inv.id)}
          data-testid="preview-invoice-btn"
        >
          <FileText className="h-3 w-3 mr-1" />
          Preview
        </Button>

        <Button
          variant="outline"
          size="sm"
          className="text-xs h-7"
          onClick={handleDownload}
          disabled={downloading === 'invoice'}
          data-testid="download-invoice-btn"
        >
          {downloading === 'invoice' ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Download className="h-3 w-3 mr-1" />}
          Download
        </Button>

        <div className="w-px h-4 bg-gray-200" />

        <Button
          variant="outline"
          size="sm"
          className="text-xs h-7 border-emerald-200 text-emerald-700 hover:bg-emerald-50"
          onClick={() => onPreviewAdvice(inv.id)}
          data-testid="preview-payment-advice-btn"
          title="Settlement Note confirming this invoice was deducted from the vendor's payout"
        >
          <FileText className="h-3 w-3 mr-1" />
          Payment Advice
        </Button>

        <Button
          variant="outline"
          size="sm"
          className="text-xs h-7 border-emerald-200 text-emerald-700 hover:bg-emerald-50"
          onClick={handleDownloadAdvice}
          disabled={downloading === 'advice'}
          data-testid="download-payment-advice-btn"
        >
          {downloading === 'advice' ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Download className="h-3 w-3 mr-1" />}
          Advice
        </Button>

        {!inv.payment_link_url && (
          <Button
            variant="outline"
            size="sm"
            className="text-xs h-7"
            onClick={handleLink}
            disabled={linking}
            data-testid="create-payment-link-btn"
          >
            {linking ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Zap className="h-3 w-3 mr-1" />}
            Create Payment Link
          </Button>
        )}

        <Button
          size="sm"
          className="text-xs h-7 ml-auto"
          onClick={handleSend}
          disabled={sending}
          data-testid="send-email-btn"
        >
          {sending ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Send className="h-3 w-3 mr-1" />}
          {inv.email_sent_at ? 'Re-send Email' : 'Send Email'}
        </Button>
      </div>

      {/* Expanded line items */}
      {expanded && (
        <div className="border-t border-gray-100 overflow-x-auto">
          <InvoiceLineItems items={inv.line_items} />
        </div>
      )}
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function CommissionInvoicesPage() {
  const { apiCall } = useAuth();

  // Controls — no default month so all historical invoices load on first visit
  const [month,       setMonth]       = useState('');
  const [vendorId,    setVendorId]    = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  // Vendor list for dropdown
  const [vendors,     setVendors]     = useState([]);
  const [vendorQ,     setVendorQ]     = useState('');

  // Invoices list
  const [invoices,    setInvoices]    = useState([]);
  const [loading,     setLoading]     = useState(false);
  const [fetchErr,    setFetchErr]    = useState(null);

  // iter-242 — Bulk selection + bulk actions (email / download) across
  // multiple invoices at once, instead of one card at a time.
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkActionRunning, setBulkActionRunning] = useState(null); // null | 'email' | 'download'
  const allSelected = invoices.length > 0 && selectedIds.length === invoices.length;
  const someSelected = selectedIds.length > 0;

  function toggleSelect(id) {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  }
  function toggleSelectAll() {
    setSelectedIds(allSelected ? [] : invoices.map(i => i.id));
  }
  function clearSelection() {
    setSelectedIds([]);
  }

  async function bulkEmailSelected() {
    setBulkActionRunning('email');
    // `sendEmail` already toasts its own per-invoice success/failure and
    // never rejects (it swallows errors internally), so this just fires
    // each one in turn and reports completion — not a trustworthy
    // ok/fail count, individual toasts already cover that.
    for (const id of selectedIds) {
      await sendEmail(id);
    }
    setBulkActionRunning(null);
    toast.success(`Bulk email complete for ${selectedIds.length} invoice${selectedIds.length !== 1 ? 's' : ''}`);
    clearSelection();
  }

  async function bulkDownloadSelected() {
    setBulkActionRunning('download');
    const targets = invoices.filter(i => selectedIds.includes(i.id));
    const BACKEND = process.env.REACT_APP_BACKEND_URL || '';
    const token = sessionStorage.getItem('token') || localStorage.getItem('token');
    const zip = new JSZip();
    let ok = 0;
    for (const inv of targets) {
      try {
        const response = await fetch(`${BACKEND}/api/admin/commission-invoices/${inv.id}/pdf`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const blob = await response.blob();
        zip.file(`${inv.invoice_number || inv.id}.pdf`, blob);
        ok++;
      } catch (e) {
        // continue — one failed invoice shouldn't block the rest
      }
    }
    if (ok > 0) {
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const url = window.URL.createObjectURL(zipBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `commission-invoices-${new Date().toISOString().slice(0, 10)}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Downloaded ${ok} invoice PDF${ok !== 1 ? 's' : ''} as a ZIP`);
    } else {
      toast.error('Failed to download any invoices');
    }
    setBulkActionRunning(null);
    clearSelection();
  }

  // iter-238 — Merchant Credit Notes (cross-month commission reversals).
  // Declared here (moved up) because the bulk-selection derived state
  // right below depends on `creditNotes` — referencing it before this
  // declaration threw "Cannot access 'creditNotes' before initialization"
  // (TDZ ReferenceError), which crashed the ENTIRE page render.
  const [creditNotes,        setCreditNotes]        = useState([]);
  const [creditNotesLoading, setCreditNotesLoading]  = useState(false);
  const [cnReloadKey,        setCnReloadKey]         = useState(0);

  // Generate states
  const [generating,  setGenerating]  = useState(false);
  const [bulkRunning, setBulkRunning] = useState(false);
  const [bulkResult,  setBulkResult]  = useState(null);

  // Uninvoiced summary
  const [activeTab,       setActiveTab]       = useState('invoices');
  const [uninvoiced,      setUninvoiced]      = useState([]);
  const [uninvoicedLoading, setUninvoicedLoading] = useState(false);
  const [quickGenerating, setQuickGenerating] = useState(null); // vendor_id currently generating

  const loadCreditNotes = useCallback(async () => {
    setCreditNotesLoading(true);
    try {
      const data = await apiCall('/api/admin/commission-invoices/credit-notes');
      setCreditNotes(Array.isArray(data) ? data : []);
    } catch {
      // non-blocking
    } finally {
      setCreditNotesLoading(false);
    }
  }, []);

  // iter-304 — Multi-month missing-invoice backfill (catches vendors
  // that had commission-bearing payouts in a PAST month but never got
  // an invoice generated for it — different from `bulkGenerate` above,
  // which only targets ONE month at a time). Preview (dry_run) first,
  // then confirm to actually write.
  const [backfillOpen,    setBackfillOpen]    = useState(false);
  const [manualDocOpen,   setManualDocOpen]   = useState(false);
  // iter-239 — Reconciliation report (Orders <-> vendor_payouts <-> Invoices)
  const [reconOpen,      setReconOpen]      = useState(false);
  const [reconLoading,   setReconLoading]   = useState(false);
  const [reconReport,    setReconReport]    = useState(null);
  const [reconMonths,    setReconMonths]    = useState(6);

  // iter-242 — Month-Bucket Reconciliation ("May orders billed under
  // June" historical correction). dry_run preview first, then Confirm
  // to atomically fix only the not-yet-emailed invoices; emailed ones
  // always go to a `manual_adjustment` report instead of being rewritten.
  const [monthBucketOpen,     setMonthBucketOpen]     = useState(false);
  const [monthBucketLoading,  setMonthBucketLoading]  = useState(false);
  const [monthBucketResult,   setMonthBucketResult]   = useState(null);

  async function runMonthBucketReconciliation(dryRun) {
    setMonthBucketLoading(true);
    try {
      const data = await apiCall(
        `/api/admin/commission-invoices/audit/month-bucket-reconciliation?dry_run=${dryRun}`,
        { method: 'POST' },
      );
      setMonthBucketResult(data);
      if (!dryRun) {
        toast.success(`Corrected ${data.rewrite_count} invoice(s); ${data.manual_adjustment_count} flagged for manual Credit/Debit Note handling`);
        loadInvoices();
      }
    } catch (e) {
      toast.error('Month-bucket reconciliation failed');
    } finally {
      setMonthBucketLoading(false);
    }
  }
  const [backfillMonths,  setBackfillMonths]  = useState(12);
  const [backfillRunning, setBackfillRunning] = useState(false);
  const [backfillPreview, setBackfillPreview] = useState(null);
  const [backfillResult,  setBackfillResult]  = useState(null);

  // iter-247b — Subscription Invoice retroactive renumbering (per
  // corrected per-billing-month sequencing — see PRD iter-247/247b).
  // Founder explicitly authorized renumbering already-issued
  // subscription invoices since they were never submitted to the GST
  // department or presented anywhere.
  const [subRenumberOpen,    setSubRenumberOpen]    = useState(false);
  const [subRenumberLoading, setSubRenumberLoading] = useState(false);
  const [subRenumberResult,  setSubRenumberResult]  = useState(null);

  async function runSubscriptionRenumbering(dryRun) {
    setSubRenumberLoading(true);
    try {
      const data = await apiCall(
        `/api/admin/subscriptions/renumber-invoices?dry_run=${dryRun}`,
        { method: 'POST' },
      );
      setSubRenumberResult(data);
      if (!dryRun) {
        toast.success(`Renumbered ${data.rewrite_count} subscription invoice(s)`);
      }
    } catch (e) {
      toast.error('Subscription invoice renumbering failed');
    } finally {
      setSubRenumberLoading(false);
    }
  }

  // iter-307e — a Razorpay transfer succeeding does NOT guarantee a
  // `vendor_payouts` row exists for it (root cause: that writer was
  // previously only invoked lazily from 2 admin-view pages). Since
  // commission-backfill reads exclusively from `vendor_payouts`, any
  // settled transfer that was never materialized stays invisible to
  // every commission invoice. This step closes that gap FIRST.
  const [syncPayoutsRunning, setSyncPayoutsRunning] = useState(false);
  const [syncPayoutsPreview, setSyncPayoutsPreview] = useState(null);
  const [syncPayoutsResult,  setSyncPayoutsResult]  = useState(null);

  // ── Fetch uninvoiced summary ───────────────────────────────────────────────
  const loadUninvoiced = useCallback(async () => {
    setUninvoicedLoading(true);
    try {
      const data = await apiCall('/api/admin/commission-invoices/uninvoiced-summary');
      setUninvoiced(Array.isArray(data) ? data : []);
    } catch {
      // non-blocking — just leaves empty
    } finally {
      setUninvoicedLoading(false);
    }
  }, []);

  // ── Fetch vendors ──────────────────────────────────────────────────────────
  useEffect(() => {
    apiCall(`/api/admin/vendor-billing/vendors?limit=300`)
      .then(d => setVendors(d.vendors || []))
      .catch(() => {});
    loadUninvoiced();
    loadCreditNotes();
  }, []);

  const filteredVendors = vendors.filter(v => {
    const q = vendorQ.toLowerCase();
    return !q ||
      (v.business_name || '').toLowerCase().includes(q) ||
      (v.email || '').toLowerCase().includes(q);
  });

  // ── Load invoices ─────────────────────────────────────────────────────────
  const loadInvoices = useCallback(async () => {
    setLoading(true);
    setFetchErr(null);
    try {
      const params = new URLSearchParams({ limit: '200' });
      if (month)       params.set('month', month);
      if (vendorId)    params.set('vendor_id', vendorId);
      if (statusFilter) params.set('status', statusFilter);
      const data = await apiCall(`/api/admin/commission-invoices/?${params}`);
      setInvoices(Array.isArray(data) ? data : []);
    } catch (e) {
      setFetchErr(e?.message || 'Failed to load invoices');
    } finally {
      setLoading(false);
    }
  }, [month, vendorId, statusFilter]);

  useEffect(() => { loadInvoices(); }, [loadInvoices]);

  // ── Generate single ───────────────────────────────────────────────────────
  async function generateInvoice() {
    if (!vendorId) {
      toast.error('Please select a vendor first');
      return;
    }
    setGenerating(true);
    try {
      const inv = await apiCall('/api/admin/commission-invoices/generate-monthly', {
        method: 'POST',
        body: JSON.stringify({
          vendor_id: vendorId,
          month,
          send_email: false,
          create_payment_link: true,
        }),
      });
      toast.success(`Invoice ${inv.invoice_number} generated — ₹${inv.grand_total}`);
      setInvoices(prev => [inv, ...prev]);
    } catch (e) {
      const msg = e?.detail?.detail || e?.detail || e?.message || 'Failed';
      toast.error(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setGenerating(false);
    }
  }

  // ── Bulk generate ─────────────────────────────────────────────────────────
  async function bulkGenerate() {
    setBulkRunning(true);
    setBulkResult(null);
    try {
      const res = await apiCall('/api/admin/commission-invoices/bulk-monthly', {
        method: 'POST',
        body: JSON.stringify({
          month,
          send_email: false,
          create_payment_link: true,
        }),
      });
      setBulkResult(res);
      toast.success(`Bulk run: ${res.summary.generated} generated, ${res.summary.skipped_no_payouts} skipped`);
      loadInvoices();
    } catch (e) {
      toast.error(e?.message || 'Bulk generation failed');
    } finally {
      setBulkRunning(false);
    }
  }

  // ── Missing-invoice backfill (multi-month, preview then confirm) ──────────
  async function previewBackfill() {
    setBackfillRunning(true);
    setBackfillPreview(null);
    setBackfillResult(null);
    try {
      const res = await apiCall(
        `/api/admin/commission-invoices/audit/commission-backfill?months=${backfillMonths}&dry_run=true`,
        { method: 'POST' }
      );
      if (!res.ok) throw new Error(res.error || 'Preview failed');
      setBackfillPreview(res);
    } catch (e) {
      toast.error(e?.message || 'Backfill preview failed');
    } finally {
      setBackfillRunning(false);
    }
  }

  async function confirmBackfill() {
    setBackfillRunning(true);
    try {
      const res = await apiCall(
        `/api/admin/commission-invoices/audit/commission-backfill?months=${backfillMonths}&dry_run=false`,
        { method: 'POST' }
      );
      if (!res.ok) throw new Error(res.error || 'Backfill failed');
      setBackfillResult(res);
      // iter-305 — the confirm result can legitimately be a "silent
      // failure" (e.g. orphaned vendor_payouts rows, or a plan/GST
      // config issue) where generated stays 0 despite Preview promising
      // some. Never show green success in that case.
      if (res.generated === 0 && res.failed > 0) {
        toast.error(`Backfill did not generate any invoices — ${res.failed} failed. See details below.`);
      } else if (res.failed > 0) {
        toast.warning(`Backfill partially completed: ${res.generated} generated, ${res.failed} failed.`);
      } else {
        toast.success(`Backfill complete: ${res.generated} invoice(s) generated, ${res.skipped} already existed`);
      }
      loadInvoices();
      loadUninvoiced();
    } catch (e) {
      toast.error(e?.message || 'Backfill failed');
    } finally {
      setBackfillRunning(false);
    }
  }

  function resetBackfillDialog() {
    setBackfillOpen(false);
    setBackfillPreview(null);
    setBackfillResult(null);
    setSyncPayoutsPreview(null);
    setSyncPayoutsResult(null);
  }

  async function previewSyncPayouts() {
    setSyncPayoutsRunning(true);
    setSyncPayoutsPreview(null);
    setSyncPayoutsResult(null);
    try {
      const res = await apiCall(
        `/api/admin/commission-invoices/audit/vendor-payouts-backfill?months=${backfillMonths}&dry_run=true`,
        { method: 'POST' }
      );
      if (!res.ok) throw new Error(res.error || 'Preview failed');
      setSyncPayoutsPreview(res);
    } catch (e) {
      toast.error(e?.message || 'Sync preview failed');
    } finally {
      setSyncPayoutsRunning(false);
    }
  }

  async function confirmSyncPayouts() {
    setSyncPayoutsRunning(true);
    try {
      const res = await apiCall(
        `/api/admin/commission-invoices/audit/vendor-payouts-backfill?months=${backfillMonths}&dry_run=false`,
        { method: 'POST' }
      );
      if (!res.ok) throw new Error(res.error || 'Sync failed');
      setSyncPayoutsResult(res);
      toast.success(
        res.generated > 0
          ? `Recovered ${res.generated} settled transfer(s) into vendor_payouts.`
          : 'No missing vendor_payouts rows found for settled transfers.'
      );
    } catch (e) {
      toast.error(e?.message || 'Sync failed');
    } finally {
      setSyncPayoutsRunning(false);
    }
  }

  // ── Send email ────────────────────────────────────────────────────────────
  async function sendEmail(invoiceId) {
    try {
      const res = await apiCall(`/api/admin/commission-invoices/${invoiceId}/send-email`, { method: 'POST' });
      if (res.success) {
        toast.success(res.message || 'Email sent');
        setInvoices(prev => prev.map(i =>
          i.id === invoiceId ? { ...i, status: 'sent', email_sent_at: new Date().toISOString() } : i
        ));
      }
    } catch (e) {
      toast.error(e?.detail || e?.message || 'Email failed');
    }
  }

  // ── Create payment link ───────────────────────────────────────────────────
  async function createPaymentLink(invoiceId) {
    try {
      const res = await apiCall(`/api/admin/commission-invoices/${invoiceId}/create-payment-link`, { method: 'POST' });
      if (res.success) {
        toast.success('Payment link created');
        setInvoices(prev => prev.map(i =>
          i.id === invoiceId
            ? { ...i, payment_link_url: res.payment_link_url, payment_link_status: 'created', payment_link_id: res.payment_link_id }
            : i
        ));
      }
    } catch (e) {
      toast.error(e?.detail || e?.message || 'Payment link creation failed');
    }
  }

  // ── Preview HTML ──────────────────────────────────────────────────────────
  // iter-301f — was a bare `window.open(apiUrl)`, which navigates the new
  // tab directly with NO Authorization header (browser tab navigation
  // can't carry a Bearer token) — the protected `/html` endpoint always
  // rejected it with 401 "Could not validate credentials", so this button
  // never worked for ANY invoice. Fetch with the auth header first, then
  // open the fetched HTML as a blob: URL (renders correctly, not a file
  // download, since the blob keeps the text/html mime type).
  async function previewInvoice(invoiceId) {
    const BACKEND = process.env.REACT_APP_BACKEND_URL || '';
    const token = sessionStorage.getItem('token') || localStorage.getItem('token');
    try {
      const response = await fetch(`${BACKEND}/api/admin/commission-invoices/${invoiceId}/html`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const html = await response.text();
      const blob = new Blob([html], { type: 'text/html' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error('Failed to load invoice preview');
    }
  }

  // ── Download PDF ─────────────────────────────────────────────────────────
  // Same auth-header-fetch pattern as previewInvoice (a bare <a href> or
  // window.open can't carry a Bearer token) — but triggers a real file
  // download (via a temporary <a download> click) instead of opening a
  // new tab, so the browser's native "Save As" / downloads-bar UX applies.
  async function downloadInvoicePdf(invoiceId, invoiceNumber) {
    const BACKEND = process.env.REACT_APP_BACKEND_URL || '';
    const token = sessionStorage.getItem('token') || localStorage.getItem('token');
    try {
      const response = await fetch(`${BACKEND}/api/admin/commission-invoices/${invoiceId}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${invoiceNumber || invoiceId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Failed to download invoice PDF');
    }
  }

  // ── Payment Advice / Settlement Note — companion document confirming
  // an invoice was settled via deduction from the vendor's payout, not
  // a separate cash/bank payment. Same auth-fetch pattern as above.
  async function previewPaymentAdvice(invoiceId) {
    const BACKEND = process.env.REACT_APP_BACKEND_URL || '';
    const token = sessionStorage.getItem('token') || localStorage.getItem('token');
    try {
      const response = await fetch(`${BACKEND}/api/admin/commission-invoices/${invoiceId}/payment-advice/html`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const html = await response.text();
      const blob = new Blob([html], { type: 'text/html' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error('Failed to load payment advice preview');
    }
  }

  async function downloadPaymentAdvicePdf(invoiceId, adviceNumber) {
    const BACKEND = process.env.REACT_APP_BACKEND_URL || '';
    const token = sessionStorage.getItem('token') || localStorage.getItem('token');
    try {
      const response = await fetch(`${BACKEND}/api/admin/commission-invoices/${invoiceId}/payment-advice/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${adviceNumber || 'payment-advice'}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Failed to download payment advice PDF');
    }
  }

  // ── Quick-generate directly from an Uninvoiced row (1-click) ──────────────
  async function quickGenerateInvoice(vid, mon) {
    setQuickGenerating(vid + mon);
    try {
      const inv = await apiCall('/api/admin/commission-invoices/generate-monthly', {
        method: 'POST',
        body: JSON.stringify({
          vendor_id: vid, month: mon, send_email: false, create_payment_link: true,
        }),
      });
      toast.success(`Invoice ${inv.invoice_number} generated — ₹${inv.grand_total}`);
      loadUninvoiced();
      if (mon === month || !month) loadInvoices();
    } catch (e) {
      const msg = e?.detail?.detail || e?.detail || e?.message || 'Failed to generate';
      toast.error(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setQuickGenerating(null);
    }
  }

  // ── Generate ALL currently-flagged (vendor, month) rows, one by one ───────
  const [generatingAllFlagged, setGeneratingAllFlagged] = useState(false);
  async function generateAllFlagged() {
    setGeneratingAllFlagged(true);
    let ok = 0, failed = 0;
    for (const r of uninvoiced) {
      try {
        await apiCall('/api/admin/commission-invoices/generate-monthly', {
          method: 'POST',
          body: JSON.stringify({
            vendor_id: r.vendor_id, month: r.month, send_email: false, create_payment_link: true,
          }),
        });
        ok++;
      } catch {
        failed++;
      }
    }
    toast[failed > 0 ? 'warning' : 'success'](`Generated ${ok} invoice(s)${failed ? `, ${failed} failed` : ''}`);
    loadUninvoiced();
    loadInvoices();
    setGeneratingAllFlagged(false);
  }

  // ── Reconciliation report (Orders <-> vendor_payouts <-> Invoices) ────────
  async function runReconciliation(dryRun) {
    setReconLoading(true);
    try {
      const report = await apiCall(
        `/api/admin/commission-invoices/audit/reconciliation?months=${reconMonths}&dry_run=${dryRun}`,
        { method: 'POST' },
      );
      setReconReport(report);
      if (!dryRun) {
        toast.success('Repair executed — re-run the check to confirm coverage');
        loadUninvoiced();
        loadInvoices();
        loadCreditNotes();
        setCnReloadKey(k => k + 1);
      }
    } catch (e) {
      toast.error('Reconciliation failed to run');
    } finally {
      setReconLoading(false);
    }
  }

  // ── Summary stats ─────────────────────────────────────────────────────────
  const totalCommission = invoices.reduce((s, i) => s + i.subtotal_commission, 0);
  const totalGst        = invoices.reduce((s, i) => s + i.subtotal_gst, 0);
  const totalDue        = invoices.reduce((s, i) => s + i.grand_total, 0);

  // ─────────────────────────────────────────────────────────────────────────

  return (
    <DashboardLayout>
    <div className="min-h-screen bg-gray-50" data-testid="commission-invoices-page">
      {/* Page header */}
      <div className="bg-white border-b border-gray-200 px-6 py-5">
        <div className="max-w-6xl mx-auto flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <Receipt className="h-5 w-5 text-gray-600" />
              Commission Invoices
            </h1>
            <p className="text-sm text-gray-500 mt-0.5">
              Monthly commission + GST invoices for vendor payouts
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => { setReconOpen(true); setReconReport(null); }}
              data-testid="open-reconciliation-dialog-btn"
            >
              <AlertCircle className="h-4 w-4 mr-1.5" />
              Reconciliation Report
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => { setMonthBucketOpen(true); setMonthBucketResult(null); }}
              data-testid="open-month-bucket-dialog-btn"
              title="Find & fix invoices whose orders belong to an earlier calendar month (e.g. May orders billed under June)"
            >
              <AlertCircle className="h-4 w-4 mr-1.5" />
              Fix Month Bucketing
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setBackfillOpen(true)}
              data-testid="open-backfill-dialog-btn"
            >
              <History className="h-4 w-4 mr-1.5" />
              Backfill Missing Invoices
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => { setSubRenumberOpen(true); setSubRenumberResult(null); }}
              data-testid="open-sub-renumber-dialog-btn"
              title="Retroactively renumber vendor SUBSCRIPTION invoices (not commission invoices) to the corrected per-billing-month sequence"
            >
              <AlertCircle className="h-4 w-4 mr-1.5" />
              Renumber Subscription Invoices
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={loadInvoices}
              disabled={loading}
              data-testid="refresh-btn"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
            <ExportLedgerButton defaultMonth={month} />
            <Button
              size="sm"
              onClick={() => setManualDocOpen(true)}
              data-testid="open-manual-document-btn"
            >
              <Plus className="h-4 w-4 mr-1.5" />
              Create Document
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-6 space-y-6">

        {/* Filters + generate controls */}
        <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-4">
          <h2 className="text-sm font-semibold text-gray-700">Generate Invoice</h2>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {/* Month picker */}
            <div>
              <label className="block text-xs text-gray-500 mb-1.5 font-medium">Month</label>
              <Input
                type="month"
                value={month}
                onChange={e => setMonth(e.target.value)}
                className="h-9 text-sm"
                data-testid="month-picker"
              />
            </div>

            {/* Vendor search */}
            <div className="sm:col-span-2">
              <label className="block text-xs text-gray-500 mb-1.5 font-medium">Vendor</label>
              <div className="flex gap-2">
                <Input
                  placeholder="Search vendor..."
                  value={vendorQ}
                  onChange={e => setVendorQ(e.target.value)}
                  className="h-9 text-sm"
                  data-testid="vendor-search"
                />
                {vendorQ && (
                  <div className="relative w-full">
                    <div className="absolute z-20 top-0 left-0 right-0 bg-white border border-gray-200
                                    rounded-xl shadow-lg max-h-52 overflow-y-auto">
                      {filteredVendors.slice(0, 20).map(v => (
                        <button
                          key={v.id}
                          className={`w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 flex justify-between items-center ${
                            vendorId === v.id ? 'bg-gray-900 text-white hover:bg-gray-800' : ''
                          }`}
                          onClick={() => { setVendorId(v.id); setVendorQ(v.business_name || v.email || ''); }}
                          data-testid={`vendor-option-${v.id}`}
                        >
                          <span>{v.business_name || v.email}</span>
                          <span className={`text-xs ${vendorId === v.id ? 'text-gray-300' : 'text-gray-400'}`}>
                            {v.plan_code}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              {vendorId && (
                <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                  <Check className="h-3 w-3" />
                  {vendors.find(v => v.id === vendorId)?.business_name || vendorId} selected
                  <button className="ml-1 text-gray-400 hover:text-gray-700 underline" onClick={() => { setVendorId(''); setVendorQ(''); }}>
                    clear
                  </button>
                </p>
              )}
            </div>
          </div>

          <div className="flex gap-3 pt-1 flex-wrap">
            <Button
              onClick={generateInvoice}
              disabled={generating || !vendorId}
              data-testid="generate-invoice-btn"
            >
              {generating
                ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Generating…</>
                : <><Receipt className="h-4 w-4 mr-2" />Generate for Selected Vendor</>}
            </Button>

            <Button
              variant="outline"
              onClick={bulkGenerate}
              disabled={bulkRunning}
              data-testid="bulk-generate-btn"
            >
              {bulkRunning
                ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Running bulk…</>
                : <><Zap className="h-4 w-4 mr-2" />Bulk Generate — All Vendors</>}
            </Button>
          </div>

          {/* Bulk result */}
          {bulkResult && (
            <div className="text-sm bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-1"
                 data-testid="bulk-result">
              <p className="font-semibold text-gray-700">Bulk run for {monthLabel(bulkResult.month)}</p>
              <p className="text-green-700">✓ Generated: {bulkResult.summary.generated}</p>
              <p className="text-gray-500">~ Already existed: {bulkResult.summary.already_exists}</p>
              <p className="text-gray-400">- No payouts: {bulkResult.summary.skipped_no_payouts}</p>
              {bulkResult.summary.errors > 0 && (
                <p className="text-red-600">✗ Errors: {bulkResult.summary.errors}</p>
              )}
            </div>
          )}
        </div>

        {/* Summary stats */}
        {invoices.length > 0 && (
          <div className="grid grid-cols-3 gap-4" data-testid="summary-stats">
            {[
              { label: 'Total Commission', value: fmt(totalCommission), sub: 'before GST' },
              { label: 'Total GST @ 18%', value: fmt(totalGst),        sub: 'SAC 996211'   },
              { label: 'Total Invoiced',  value: fmt(totalDue),         sub: `${invoices.length} invoices` },
            ].map(s => (
              <div key={s.label} className="bg-white border border-gray-200 rounded-xl px-5 py-4">
                <p className="text-xs text-gray-500 font-medium">{s.label}</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{s.value}</p>
                <p className="text-xs text-gray-400 mt-0.5">{s.sub}</p>
              </div>
            ))}
          </div>
        )}

        {/* Tab switcher */}
        <div className="flex gap-2 border-b border-gray-200 pb-0" data-testid="invoice-tabs">
          {[
            { id: 'invoices', label: 'Commission Invoices', count: invoices.length },
            { id: 'pending',  label: 'Pending (Uninvoiced)', count: uninvoiced.length, accent: uninvoiced.length > 0 },
            { id: 'credit_notes', label: 'Credit Notes', count: creditNotes.length },
          ].map(t => (
            <button
              key={t.id}
              onClick={() => setActiveTab(t.id)}
              data-testid={`tab-${t.id}`}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                activeTab === t.id
                  ? 'border-gray-900 text-gray-900'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {t.label}
              <span className={`text-xs rounded-full px-1.5 py-0.5 font-semibold ${
                t.accent ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-500'
              }`}>{t.count}</span>
            </button>
          ))}
        </div>

        {/* Status filter (only for invoices tab) */}
        {activeTab === 'invoices' && (
        <div className="flex gap-2 flex-wrap">
          {['', 'finalized', 'sent', 'paid'].map(s => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                statusFilter === s
                  ? 'bg-gray-900 text-white border-gray-900'
                  : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'
              }`}
              data-testid={`status-filter-${s || 'all'}`}
            >
              {s || 'All'}
            </button>
          ))}
        </div>
        )}

        {/* ─── Invoices tab ───────────────────────────────────────────────────── */}
        {activeTab === 'invoices' && (
        <>

        {/* Invoices list */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
          </div>
        )}

        {fetchErr && (
          <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-4"
               data-testid="fetch-error">
            <AlertCircle className="h-4 w-4 shrink-0" />
            {fetchErr}
          </div>
        )}

        {!loading && !fetchErr && invoices.length === 0 && (
          <div className="text-center py-16 text-gray-400" data-testid="empty-state">
            <Receipt className="h-10 w-10 mx-auto mb-3 opacity-30" />
            <p className="text-sm font-medium">
              {month ? `No invoices for ${monthLabel(month)}` : 'No commission invoices found'}
            </p>
            <p className="text-xs mt-1">
              {vendorId
                ? 'This vendor has no completed payouts for the selected period.'
                : month
                  ? 'Click "Bulk Generate" to generate for all vendors.'
                  : 'No invoices have been generated yet. Use "Bulk Generate" to create them.'}
            </p>
          </div>
        )}

        {/* Bulk selection toolbar */}
        {!loading && invoices.length > 0 && (
          <div className="flex items-center gap-3 flex-wrap bg-gray-50 border border-gray-200 rounded-lg px-4 py-2.5"
               data-testid="bulk-action-bar">
            <div className="flex items-center gap-2">
              <Checkbox
                checked={allSelected}
                onCheckedChange={toggleSelectAll}
                data-testid="select-all-invoices"
              />
              <span className="text-xs font-medium text-gray-600">
                {someSelected ? `${selectedIds.length} of ${invoices.length} selected` : 'Select all'}
              </span>
            </div>

            {someSelected && (
              <div className="flex items-center gap-2 ml-auto">
                <Button
                  variant="outline" size="sm" className="text-xs h-7"
                  onClick={bulkDownloadSelected}
                  disabled={bulkActionRunning !== null}
                  data-testid="bulk-download-btn"
                >
                  {bulkActionRunning === 'download'
                    ? <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                    : <Download className="h-3 w-3 mr-1" />}
                  Download {selectedIds.length} as ZIP
                </Button>
                <Button
                  size="sm" className="text-xs h-7"
                  onClick={bulkEmailSelected}
                  disabled={bulkActionRunning !== null}
                  data-testid="bulk-email-btn"
                >
                  {bulkActionRunning === 'email'
                    ? <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                    : <Send className="h-3 w-3 mr-1" />}
                  Email {selectedIds.length} Vendor{selectedIds.length !== 1 ? 's' : ''}
                </Button>
                <Button
                  variant="ghost" size="sm" className="text-xs h-7 text-gray-500"
                  onClick={clearSelection}
                  data-testid="bulk-clear-btn"
                >
                  Clear
                </Button>
              </div>
            )}
          </div>
        )}

        <div className="space-y-3">
          {invoices.map(inv => (
            <InvoiceCard
              key={inv.id}
              inv={inv}
              onSendEmail={sendEmail}
              onCreateLink={createPaymentLink}
              onPreview={previewInvoice}
              onDownload={downloadInvoicePdf}
              onPreviewAdvice={previewPaymentAdvice}
              onDownloadAdvice={downloadPaymentAdvicePdf}
              selected={selectedIds.includes(inv.id)}
              onToggleSelect={toggleSelect}
            />
          ))}
        </div>

        </> /* end invoices tab */
        )}

        {/* ─── Pending / Uninvoiced tab ────────────────────────────────────── */}
        {activeTab === 'pending' && (
          <UninvoicedSection
            rows={uninvoiced}
            loading={uninvoicedLoading}
            onRefresh={loadUninvoiced}
            onGenerate={quickGenerateInvoice}
            generatingKey={quickGenerating}
            onGenerateAll={generateAllFlagged}
            generatingAll={generatingAllFlagged}
          />
        )}

        {/* ─── Credit Notes tab ───────────────────────────────────────────── */}
        {activeTab === 'credit_notes' && (
          <CommissionCreditNotesTab reloadSignal={cnReloadKey} />
        )}

      </div>
    </div>

    <ManualCommissionDocumentModal
      open={manualDocOpen}
      onOpenChange={setManualDocOpen}
      onCreated={() => { setCnReloadKey(k => k + 1); loadInvoices(); }}
    />

    {/* Multi-month missing-invoice backfill dialog */}
    <Dialog open={backfillOpen} onOpenChange={(open) => { if (!open) resetBackfillDialog(); else setBackfillOpen(true); }}>
      <DialogContent className="max-w-2xl" data-testid="backfill-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="h-4 w-4" /> Backfill Missing Commission Invoices
          </DialogTitle>
          <DialogDescription>
            Scans past months of vendor payouts for any vendor that had commission-bearing
            orders but never got a monthly invoice generated. Preview first — nothing is
            written until you confirm.
          </DialogDescription>
        </DialogHeader>

        <div className="border border-blue-200 rounded-lg bg-blue-50/60 p-3 space-y-2" data-testid="sync-payouts-section">
          <p className="text-xs font-semibold text-blue-800 uppercase tracking-wide">
            Step 1 · Sync settled transfers into vendor_payouts (run this first)
          </p>
          <p className="text-xs text-blue-700">
            A settled Razorpay transfer doesn't always leave a local vendor_payouts row (fixed for
            future transfers) — this recovers past ones so Step 2 below can see them.
          </p>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={previewSyncPayouts}
              disabled={syncPayoutsRunning}
              data-testid="sync-payouts-preview-btn"
            >
              {syncPayoutsRunning ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <PlayCircle className="h-3.5 w-3.5 mr-1.5" />}
              Preview
            </Button>
            {syncPayoutsPreview && !syncPayoutsResult && (
              <>
                <span className="text-xs text-blue-800">
                  {syncPayoutsPreview.generated} missing · {syncPayoutsPreview.skipped} already synced
                </span>
                {syncPayoutsPreview.generated > 0 && (
                  <Button
                    size="sm"
                    onClick={confirmSyncPayouts}
                    disabled={syncPayoutsRunning}
                    data-testid="sync-payouts-confirm-btn"
                  >
                    Confirm &amp; Recover {syncPayoutsPreview.generated}
                  </Button>
                )}
              </>
            )}
            {syncPayoutsResult && (
              <span className="text-xs text-green-700 font-medium" data-testid="sync-payouts-result">
                Done — {syncPayoutsResult.generated} recovered, {syncPayoutsResult.skipped} already synced,
                {' '}{syncPayoutsResult.failed} failed.
              </span>
            )}
          </div>
        </div>

        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide pt-1">
          Step 2 · Generate missing commission invoices
        </p>
        <div className="flex items-center gap-3">
          <label className="text-sm text-gray-600 whitespace-nowrap">Months to scan back:</label>
          <Input
            type="number"
            min={1}
            max={36}
            value={backfillMonths}
            onChange={(e) => setBackfillMonths(Math.max(1, Math.min(36, Number(e.target.value) || 1)))}
            className="w-24"
            data-testid="backfill-months-input"
            disabled={backfillRunning}
          />
          <Button
            size="sm"
            variant="outline"
            onClick={previewBackfill}
            disabled={backfillRunning}
            data-testid="backfill-preview-btn"
          >
            {backfillRunning ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <PlayCircle className="h-3.5 w-3.5 mr-1.5" />}
            Preview
          </Button>
        </div>

        {backfillPreview && !backfillResult && (
          <div className="border border-gray-200 rounded-lg overflow-hidden" data-testid="backfill-preview-result">
            <div className="px-3 py-2 bg-amber-50 border-b border-amber-100 text-sm text-amber-800 flex items-center justify-between">
              <span>
                Would generate <strong>{backfillPreview.generated}</strong> invoice(s) ·
                {' '}{backfillPreview.skipped} already existed
                {backfillPreview.orphaned_vendor_payouts > 0 && (
                  <> · <span className="text-red-700">{backfillPreview.orphaned_vendor_payouts} orphaned (vendor deleted, skipped)</span></>
                )}
              </span>
            </div>
            <div className="max-h-56 overflow-y-auto divide-y divide-gray-100">
              {backfillPreview.summary.filter(m => m.vendors_seen > 0).map(m => (
                <div key={m.month} className="px-3 py-2 text-xs flex items-center justify-between">
                  <span className="font-mono text-gray-600">{monthLabel(m.month)}</span>
                  <span className="text-gray-500">
                    {m.generated} to generate · {m.skipped} existing
                    {m.orphaned_vendor_payouts > 0 && (
                      <span className="text-red-600"> · {m.orphaned_vendor_payouts} orphaned</span>
                    )}
                  </span>
                </div>
              ))}
              {backfillPreview.summary.every(m => m.vendors_seen === 0) && (
                <p className="px-3 py-4 text-sm text-gray-400 text-center">
                  Nothing to backfill — every vendor with commission-bearing payouts already
                  has an invoice for every month scanned.
                </p>
              )}
            </div>
          </div>
        )}

        {backfillResult && (() => {
          const hadFailures = backfillResult.failed > 0;
          const wasNoOp = backfillResult.generated === 0 && hadFailures;
          const tone = wasNoOp
            ? { border: 'border-red-200', bg: 'bg-red-50', text: 'text-red-800', label: 'Backfill did not generate any invoices.' }
            : hadFailures
              ? { border: 'border-amber-200', bg: 'bg-amber-50', text: 'text-amber-800', label: 'Backfill partially completed.' }
              : { border: 'border-green-200', bg: 'bg-green-50', text: 'text-green-800', label: 'Backfill complete.' };
          return (
            <div className={`border ${tone.border} rounded-lg ${tone.bg} px-3 py-3 text-sm ${tone.text}`} data-testid="backfill-confirm-result">
              <p className="font-semibold">{tone.label}</p>
              <p className="mt-1">
                {backfillResult.generated} invoice(s) generated · {backfillResult.skipped} already existed ·
                {' '}{backfillResult.failed} failed
                {backfillResult.orphaned_vendor_payouts > 0 && (
                  <> · {backfillResult.orphaned_vendor_payouts} orphaned vendor_payouts (vendor deleted)</>
                )}.
              </p>

              {/* iter-242 — was previously just a bare count with NO way
                  to see WHY an item failed. Surface the exact vendor +
                  month + underlying exception message for every failure. */}
              {(backfillResult.failures || []).length > 0 && (
                <div className="mt-3 border-t border-current/20 pt-2 space-y-1.5" data-testid="backfill-failure-details">
                  <p className="text-xs font-semibold uppercase tracking-wide opacity-70">Failure Details</p>
                  {backfillResult.failures.map((f, i) => (
                    <div key={i} className="bg-white/60 rounded px-2.5 py-2 text-xs" data-testid={`backfill-failure-${i}`}>
                      <p className="font-medium">
                        {f.vendor_name || f.vendor_id} {f.vendor_email && <span className="opacity-70">({f.vendor_email})</span>}
                        <span className="opacity-70"> · {f.month}</span>
                      </p>
                      <p className="mt-0.5 font-mono text-[11px] opacity-90">
                        {f.status === 'orphaned_vendor_payouts'
                          ? (f.note || 'Vendor record no longer exists — payout data is orphaned.')
                          : (f.error || 'Unknown error')}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })()}

        <DialogFooter>
          <Button variant="outline" onClick={resetBackfillDialog} data-testid="backfill-close-btn">
            Close
          </Button>
          {backfillPreview && !backfillResult && backfillPreview.generated > 0 && (
            <Button
              onClick={confirmBackfill}
              disabled={backfillRunning}
              data-testid="backfill-confirm-btn"
            >
              {backfillRunning ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : null}
              Confirm &amp; Generate {backfillPreview.generated} Invoice(s)
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* ─── Fix Month Bucketing Dialog (iter-242) ─────────────────────────── */}
    <Dialog open={monthBucketOpen} onOpenChange={(open) => { setMonthBucketOpen(open); if (!open) setMonthBucketResult(null); }}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="month-bucket-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertCircle className="h-4 w-4" /> Fix Month Bucketing
          </DialogTitle>
          <DialogDescription>
            Finds orders billed on the wrong month's Tax Invoice (e.g. a May
            order whose payout row wasn't created until June). Already-emailed
            invoices are NEVER rewritten — they're listed below for a manual
            Credit/Debit Note instead. Not-yet-emailed invoices are corrected
            automatically when you click Confirm.
          </DialogDescription>
        </DialogHeader>

        {!monthBucketResult && (
          <Button
            onClick={() => runMonthBucketReconciliation(true)}
            disabled={monthBucketLoading}
            data-testid="month-bucket-preview-btn"
          >
            {monthBucketLoading ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : null}
            Run Dry-Run Preview
          </Button>
        )}

        {monthBucketResult && (
          <div className="space-y-4" data-testid="month-bucket-result">
            <p className="text-sm text-gray-600">
              {monthBucketResult.dry_run ? 'Preview' : 'Correction applied'} —{' '}
              <strong>{monthBucketResult.rewrite_count}</strong> invoice(s){monthBucketResult.dry_run ? ' can be' : ' were'} auto-corrected,{' '}
              <strong>{monthBucketResult.manual_adjustment_count}</strong> order(s) need a manual Credit/Debit Note (already emailed).
            </p>

            {monthBucketResult.rewrite_preview && monthBucketResult.rewrite_preview.length > 0 && (
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1.5">Will Auto-Correct</p>
                <div className="space-y-1.5">
                  {monthBucketResult.rewrite_preview.map((rc, i) => (
                    <div key={i} className="text-xs bg-blue-50 border border-blue-100 rounded px-2.5 py-2" data-testid={`month-bucket-rewrite-${i}`}>
                      <strong>{rc.invoice_number}</strong> ({rc.vendor_business_name}, {monthLabel(rc.invoice_month)}) —{' '}
                      moving {rc.orders_moving_out} order(s) to {rc.moving_to_months.map(m => monthLabel(m)).join(', ')}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {monthBucketResult.executed && monthBucketResult.executed.length > 0 && (
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1.5">Corrected</p>
                <div className="space-y-1.5">
                  {monthBucketResult.executed.map((ex, i) => (
                    <div key={i} className="text-xs bg-green-50 border border-green-100 rounded px-2.5 py-2" data-testid={`month-bucket-executed-${i}`}>
                      {ex.action === 'created_new' && <>Created <strong>{ex.new_invoice}</strong> for {monthLabel(ex.month)} ({ex.orders_moved} order(s))</>}
                      {ex.action === 'merged_into_existing' && <>Merged {ex.orders_moved} order(s) into existing <strong>{ex.target_invoice}</strong> ({monthLabel(ex.month)})</>}
                      {ex.action === 'trimmed' && <>Trimmed <strong>{ex.invoice_number}</strong> — {ex.remaining_orders} order(s) remain</>}
                      {ex.action === 'deleted_empty' && <>Removed empty invoice <strong>{ex.invoice_number}</strong> (all orders moved out)</>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {monthBucketResult.manual_adjustment && monthBucketResult.manual_adjustment.length > 0 && (
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1.5">
                  Manual Adjustment Report (already emailed — do not rewrite)
                </p>
                <div className="space-y-1.5">
                  {monthBucketResult.manual_adjustment.map((m, i) => (
                    <div key={i} className="text-xs bg-amber-50 border border-amber-100 rounded px-2.5 py-2" data-testid={`month-bucket-manual-${i}`}>
                      Order <strong>{m.order_number || m.order_id}</strong> ({monthLabel(m.correct_order_month)}) is on{' '}
                      <strong>{m.invoice_number}</strong> ({monthLabel(m.invoice_month)}, {m.vendor_business_name}) —{' '}
                      GST paid: ₹{(m.commission_gst || 0).toFixed(2)}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => { setMonthBucketOpen(false); setMonthBucketResult(null); }} data-testid="month-bucket-close-btn">
            Close
          </Button>
          {monthBucketResult && monthBucketResult.dry_run && monthBucketResult.rewrite_count > 0 && (
            <Button
              onClick={() => runMonthBucketReconciliation(false)}
              disabled={monthBucketLoading}
              data-testid="month-bucket-confirm-btn"
            >
              {monthBucketLoading ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : null}
              Confirm &amp; Correct {monthBucketResult.rewrite_count} Invoice(s)
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* ─── Renumber Subscription Invoices Dialog (iter-247b) ────────────── */}
    <Dialog open={subRenumberOpen} onOpenChange={(open) => { setSubRenumberOpen(open); if (!open) setSubRenumberResult(null); }}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="sub-renumber-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertCircle className="h-4 w-4" /> Renumber Subscription Invoices
          </DialogTitle>
          <DialogDescription>
            Retroactively fixes vendor SUBSCRIPTION invoice numbers (plan billing —
            not commission) to the corrected per-billing-month sequence
            (e.g. <code>SUB0626-0001</code> for June 2026) and corrects the SAC
            code to 998316. Authorized because these invoices have not yet been
            submitted to the GST department or presented anywhere. Idempotent —
            safe to re-run; already-correct invoices are left untouched.
          </DialogDescription>
        </DialogHeader>

        {!subRenumberResult && (
          <Button
            onClick={() => runSubscriptionRenumbering(true)}
            disabled={subRenumberLoading}
            data-testid="sub-renumber-preview-btn"
          >
            {subRenumberLoading ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : null}
            Run Dry-Run Preview
          </Button>
        )}

        {subRenumberResult && (
          <div className="space-y-4" data-testid="sub-renumber-result">
            <p className="text-sm text-gray-600">
              {subRenumberResult.dry_run ? 'Preview' : 'Correction applied'} —{' '}
              <strong>{subRenumberResult.rewrite_count}</strong> of{' '}
              <strong>{subRenumberResult.total_invoices}</strong> invoice(s)
              {subRenumberResult.dry_run ? ' will be' : ' were'} renumbered/SAC-corrected.
              {!subRenumberResult.dry_run && (
                <> {subRenumberResult.credit_notes_updated || 0} credit note reference(s) updated.</>
              )}
            </p>

            {(subRenumberResult.rewrite_preview || subRenumberResult.executed || []).length > 0 && (
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1.5">
                  {subRenumberResult.dry_run ? 'Will Renumber' : 'Renumbered'}
                </p>
                <div className="space-y-1.5 max-h-64 overflow-y-auto">
                  {(subRenumberResult.dry_run ? subRenumberResult.rewrite_preview : subRenumberResult.executed).map((rw, i) => (
                    <div key={i} className="text-xs bg-blue-50 border border-blue-100 rounded px-2.5 py-2" data-testid={`sub-renumber-row-${i}`}>
                      <strong>{rw.old_number}</strong> &rarr; <strong>{rw.new_number}</strong>{' '}
                      ({rw.period_label}){rw.sac_corrected ? ' — SAC corrected' : ''}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => { setSubRenumberOpen(false); setSubRenumberResult(null); }} data-testid="sub-renumber-close-btn">
            Close
          </Button>
          {subRenumberResult && subRenumberResult.dry_run && subRenumberResult.rewrite_count > 0 && (
            <Button
              onClick={() => runSubscriptionRenumbering(false)}
              disabled={subRenumberLoading}
              data-testid="sub-renumber-confirm-btn"
            >
              {subRenumberLoading ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : null}
              Confirm &amp; Renumber {subRenumberResult.rewrite_count} Invoice(s)
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* ─── Reconciliation Report Dialog (iter-239) ──────────────────────── */}
    <Dialog open={reconOpen} onOpenChange={setReconOpen}>
      <DialogContent className="max-w-2xl" data-testid="reconciliation-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertCircle className="h-4 w-4" /> Commission Reconciliation Report
          </DialogTitle>
          <DialogDescription>
            Orders ↔ vendor_payouts ↔ Commission Invoices — checks the currently
            connected database (works identically in preview or production).
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-3">
          <label className="text-sm text-gray-500">Lookback window</label>
          <select
            value={reconMonths}
            onChange={e => setReconMonths(Number(e.target.value))}
            className="text-sm border border-gray-300 rounded-md px-2 py-1"
            data-testid="recon-months-select"
          >
            {[3, 6, 12, 24].map(m => <option key={m} value={m}>{m} months</option>)}
          </select>
          <Button
            size="sm"
            onClick={() => runReconciliation(true)}
            disabled={reconLoading}
            data-testid="run-reconciliation-dry-run-btn"
          >
            {reconLoading ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : null}
            Run Check (Dry Run)
          </Button>
        </div>

        {reconReport && (
          <div className="space-y-2 text-sm" data-testid="reconciliation-report">
            <div className={`px-3 py-2 rounded-lg font-semibold ${reconReport.coverage_complete ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'}`}>
              {reconReport.coverage_complete
                ? '✓ 100% coverage — no gaps found'
                : 'Gaps found — review below, then execute the repair'}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="border border-gray-100 rounded-lg px-3 py-2">
                <p className="text-xs text-gray-400">Total Settled Orders</p>
                <p className="text-lg font-bold">{reconReport.total_settled_orders}</p>
              </div>
              <div className="border border-gray-100 rounded-lg px-3 py-2">
                <p className="text-xs text-gray-400">Missing vendor_payouts</p>
                <p className="text-lg font-bold" data-testid="recon-missing-payouts-count">
                  {reconReport.orders_missing_vendor_payouts.count}
                </p>
              </div>
              <div className="border border-gray-100 rounded-lg px-3 py-2">
                <p className="text-xs text-gray-400">Payouts Missing Invoices</p>
                <p className="text-lg font-bold" data-testid="recon-missing-invoices-count">
                  {reconReport.payouts_missing_invoices.count}
                </p>
              </div>
              <div className="border border-gray-100 rounded-lg px-3 py-2">
                <p className="text-xs text-gray-400">₹0 Commission (excluded)</p>
                <p className="text-lg font-bold">{reconReport.zero_commission_exclusions.count}</p>
              </div>
              <div className="border border-gray-100 rounded-lg px-3 py-2 col-span-2">
                <p className="text-xs text-gray-400">Amount Mismatches (recomputed vs stored)</p>
                <p className="text-lg font-bold">{reconReport.amount_mismatches.count}</p>
              </div>
            </div>
            {!reconReport.dry_run && (
              <div className="text-xs text-gray-500 border border-gray-100 rounded-lg px-3 py-2">
                Repair executed: {reconReport.orders_missing_vendor_payouts.reconstructed.length} payout(s) reconstructed ·
                {' '}{reconReport.payouts_missing_invoices.invoices_generated.length} invoice(s) generated ·
                {' '}{reconReport.payouts_missing_invoices.debit_notes_issued.length} debit note(s) issued
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => setReconOpen(false)} data-testid="reconciliation-close-btn">
            Close
          </Button>
          {reconReport && !reconReport.coverage_complete && (
            <Button
              onClick={() => runReconciliation(false)}
              disabled={reconLoading}
              data-testid="run-reconciliation-execute-btn"
            >
              {reconLoading ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : null}
              Execute Repair
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </DashboardLayout>
  );
}

// ─── Uninvoiced payouts section ───────────────────────────────────────────────

function UninvoicedSection({ rows, loading, onRefresh, onGenerate, generatingKey, onGenerateAll, generatingAll }) {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }

  if (!rows.length) {
    return (
      <div className="text-center py-16 text-gray-400" data-testid="uninvoiced-empty">
        <Clock className="h-10 w-10 mx-auto mb-3 opacity-30" />
        <p className="text-sm font-medium text-gray-500">All payouts are invoiced</p>
        <p className="text-xs mt-1">No pending commissions found in vendor_payouts.</p>
      </div>
    );
  }

  // Group by month
  const byMonth = {};
  for (const r of rows) {
    if (!byMonth[r.month]) byMonth[r.month] = [];
    byMonth[r.month].push(r);
  }
  const months = Object.keys(byMonth).sort((a, b) => b.localeCompare(a));

  const totalCommission = rows.reduce((s, r) => s + (r.commission || 0), 0);
  const totalGst        = rows.reduce((s, r) => s + (r.commission_gst || 0), 0);
  const totalNet        = rows.reduce((s, r) => s + (r.net_payout || 0), 0);

  return (
    <div className="space-y-5" data-testid="uninvoiced-section">
      {/* Summary row */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: 'Total Commission', value: fmt(totalCommission), sub: `${rows.length} vendor-months` },
          { label: 'GST @ 18%',        value: fmt(totalGst),        sub: 'SAC 996211'                    },
          { label: 'Total Net Payout', value: fmt(totalNet),        sub: 'to be disbursed'             },
        ].map(s => (
          <div key={s.label} className="bg-white border border-gray-200 rounded-xl px-5 py-4">
            <p className="text-xs text-gray-500 font-medium">{s.label}</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">{s.value}</p>
            <p className="text-xs text-gray-400 mt-0.5">{s.sub}</p>
          </div>
        ))}
      </div>

      <div className="flex justify-end items-center gap-3">
        <button onClick={onRefresh} className="text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1">
          <RefreshCw className="h-3 w-3" /> Refresh
        </button>
        <Button
          size="sm"
          onClick={onGenerateAll}
          disabled={generatingAll}
          data-testid="generate-all-flagged-btn"
        >
          {generatingAll
            ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />Generating…</>
            : <><Zap className="h-3.5 w-3.5 mr-1.5" />Generate All ({rows.length})</>}
        </Button>
      </div>

      {/* Month groups */}
      {months.map(mon => {
        const items = byMonth[mon];
        const monTotal = items.reduce((s, r) => s + (r.commission || 0), 0);
        return (
          <div key={mon} className="border border-amber-100 rounded-xl overflow-hidden bg-white">
            <div className="flex items-center justify-between px-4 py-3 bg-amber-50 border-b border-amber-100">
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-amber-500" />
                <span className="font-semibold text-sm text-gray-700">{monthLabel(mon)}</span>
                <span className="text-xs text-gray-400">{items.length} vendor{items.length !== 1 ? 's' : ''}</span>
              </div>
              <span className="text-sm font-semibold text-amber-700">{fmt(monTotal)} commission</span>
            </div>
            <div className="divide-y divide-gray-100">
              {items.map((r, i) => (
                <div key={i} className="flex items-center justify-between px-4 py-3 text-sm hover:bg-gray-50" data-testid={`uninvoiced-row-${r.vendor_id}`}>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-gray-900 truncate">{r.vendor_name || r.vendor_id}</p>
                    <p className="text-xs text-gray-400">{r.order_count} order{r.order_count !== 1 ? 's' : ''}</p>
                  </div>
                  <div className="flex items-center gap-4 shrink-0">
                    <div className="text-right">
                      <p className="text-xs text-gray-400">Commission</p>
                      <p className="font-semibold text-gray-700">{fmt(r.commission)}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-gray-400">Net payout</p>
                      <p className="font-semibold text-gray-700">{fmt(r.net_payout)}</p>
                    </div>
                    <button
                      onClick={() => onGenerate(r.vendor_id, mon)}
                      disabled={generatingKey === r.vendor_id + mon}
                      className="text-xs px-3 py-1.5 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 hover:border-gray-400 transition-colors whitespace-nowrap disabled:opacity-50"
                      data-testid={`quick-generate-${r.vendor_id}`}
                    >
                      {generatingKey === r.vendor_id + mon
                        ? <Loader2 className="h-3 w-3 animate-spin inline mr-1" />
                        : null}
                      Generate Invoice
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Merchant Credit Note card ─────────────────────────────────────────────
// (moved to shared component `components/admin/CommissionCreditNotesTab.jsx`
// — used identically here and on Admin -> Vendor Commissions)
